import { FC, useCallback, useMemo, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { LightTooltip } from "@/components/LightTooltip";
import { useCompanyPageStore } from "@/store/companyPageStore";
import {
  useGetCompanyIntentQuery,
  SortDir,
  InclusionMethod,
} from "@/generated/company-search";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import { DataTable, useDataTableState } from "@/components/ui/DataTable";
import { formatNumber } from "@/utils/numberUtils";
import { emptyToUndefined } from "@/utils/arrayUtils";
import { EmptyState } from "../../../components/EmptyState";
import { getIntentColumns } from "./columns";
import { useIntentFiltersStorage } from "./hooks/useIntentFiltersStorage";
import {
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
  INTENT_TABLE_SORT_FIELDS,
  DEFAULT_NUMBER_OF_CADENCES,
  getCadenceLabel,
} from "./constants";
import type { IntentSignal } from "./types";

const IntentEmptyOverlay = () => (
  <EmptyState
    title="No intent activity found"
    description="No intent data found for this company at this time"
    icon={<TrendingUpIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />}
    height={400}
  />
);

const IntentErrorOverlay = () => (
  <EmptyState
    title="Failed to load intent data"
    description="An error occurred while fetching intent activity. Please try again."
    icon={<ErrorOutlineIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />}
    height={400}
  />
);

type IntentRow = IntentSignal & { id: number };

/**
 * Intent Activity table component
 * Displays intent signals with pagination, sorting, and row selection
 */
export const IntentTable: FC = () => {
  const company = useCompanyPageStore((state) => state.company);
  const setIntentSignalDetailsOpen = useCompanyPageStore(
    (state) => state.setIntentSignalDetailsOpen
  );
  const setSelectedIntentSignal = useCompanyPageStore(
    (state) => state.setSelectedIntentSignal
  );
  const { intentFilters } = useIntentFiltersStorage();

  // Table state managed by hook
  const tableState = useDataTableState({
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSort: [
      { field: INTENT_TABLE_SORT_FIELDS.SIGNAL_STRENGTH, sort: "desc" },
    ],
    resetPageOnSort: true,
  });

  const variables = useMemo(() => {
    if (!company?.hgId) return null;

    const sortField =
      tableState.sortModel[0]?.field ??
      INTENT_TABLE_SORT_FIELDS.SIGNAL_STRENGTH;
    const sortDirection =
      tableState.sortModel[0]?.sort === "asc" ? SortDir.ASC : SortDir.DESC;

    const hasLocation =
      intentFilters.locationCountryAlpha2s.length > 0 ||
      intentFilters.locationRegionNames.length > 0 ||
      intentFilters.locationStateIds.length > 0;

    return {
      hgIds: [company.hgId],
      includeCorporateDescendants: intentFilters.includeCorporateDescendants,
      urlInstallIds: company.urlInstallId ? [company.urlInstallId] : undefined,
      page: tableState.paginationModel.page,
      per: tableState.paginationModel.pageSize,
      sort: [{ field: sortField, direction: sortDirection }],
      intent: {
        topics: {
          ids: intentFilters.topicIds,
          inclusionMethod: InclusionMethod.ANY_PRESENT,
        },
        buyersJourneyIds: emptyToUndefined(intentFilters.buyersJourneyIds),
        contextTypeIds: emptyToUndefined(intentFilters.contextTypeIds),
        location: hasLocation
          ? {
              countryAlpha2s: emptyToUndefined(
                intentFilters.locationCountryAlpha2s
              ),
              regionNames: emptyToUndefined(intentFilters.locationRegionNames),
              stateIds: emptyToUndefined(intentFilters.locationStateIds),
              excludedCountryAlpha2s: undefined,
            }
          : undefined,
      },
      ignoreProvisioning: true,
      techTopicsOnly: intentFilters.techTopicsOnly || undefined,
    };
  }, [
    company?.hgId,
    company?.urlInstallId,
    intentFilters,
    tableState.paginationModel,
    tableState.sortModel,
  ]);

  const { data, loading, error } = useGetCompanyIntentQuery({
    variables: variables!,
    skip: !variables,
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const tableData: IntentRow[] = useMemo(() => {
    const rawData = (data?.intent?.data ?? []) as IntentSignal[];
    return rawData.map((row, index) => ({ ...row, id: index }));
  }, [data]);

  // Preserve count during loading to prevent DataGrid from resetting pagination
  const rawCount = data?.intent?.count;
  const [lastCount, setLastCount] = useState<number>(0);

  useEffect(() => {
    if (rawCount !== undefined && rawCount !== null) {
      setLastCount(rawCount);
    }
  }, [rawCount]);

  const count = loading ? lastCount : (rawCount ?? 0);

  const handleRowClick = useCallback(
    (signal: IntentSignal) => {
      setSelectedIntentSignal(signal);
      setIntentSignalDetailsOpen(true);
    },
    [setSelectedIntentSignal, setIntentSignalDetailsOpen]
  );

  const columns = getIntentColumns();

  const cadenceLabel = getCadenceLabel(DEFAULT_NUMBER_OF_CADENCES);
  const companyDomain = company?.url || company?.name || "this company";
  const tooltipText = `Showing all available intent signals associated with ${companyDomain} in ${cadenceLabel}`;

  const countLabel = useMemo(() => {
    if (count > tableState.paginationModel.pageSize) {
      return `Top ${formatNumber(tableState.paginationModel.pageSize)} of ${formatNumber(count)}`;
    }
    return formatNumber(count);
  }, [count, tableState.paginationModel.pageSize]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        height={24}
        mb={2}
        spacing={0.5}
      >
        {loading ? (
          <>
            <Skeleton variant="text" width={200} height={40} />
            <Typography variant="h5">
              Topics searched for in {cadenceLabel}
            </Typography>
          </>
        ) : (
          <Typography variant="h5">
            {`${countLabel} Topics searched for in ${cadenceLabel}`}
          </Typography>
        )}
        <LightTooltip title={tooltipText}>
          <IconButton size="small" sx={{ p: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </IconButton>
        </LightTooltip>
      </Stack>

      <Box
        data-testid="company-detail-intent-table"
        sx={{ height: "100%", width: "100%", marginLeft: -1 }}
      >
        <DataTable
          rows={tableData}
          columns={columns}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getRowId={(row: any) => row.id}
          rowCount={count}
          paginationModel={tableState.paginationModel}
          onPaginationModelChange={tableState.onPaginationModelChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sortModel={tableState.sortModel}
          onSortModelChange={tableState.onSortModelChange}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          slots={{ empty: IntentEmptyOverlay, error: IntentErrorOverlay }}
        />
      </Box>
    </Box>
  );
};
